import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  User, Mail, CreditCard, Calendar, Gift, AlertTriangle, 
  Check, X, Loader2, ArrowLeft, LogOut, Lock 
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { Input } from '../components/ui/input';
import useAuthStore from '../store/useAuthStore';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Account = () => {
  const navigate = useNavigate();
  const { user, token, logout, refreshUser } = useAuthStore();
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [cancelSuccess, setCancelSuccess] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState(null);

  const handleCancelSubscription = async () => {
    setCancelling(true);
    setCancelError('');
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/subscription/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to cancel subscription');
      }
      
      setCancelSuccess(true);
      await refreshUser();
      
      // Redirect after a moment
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error) {
      setCancelError(error.message);
    }
    setCancelling(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleChangePassword = async () => {
    setPasswordMsg(null);
    if (newPassword.length < 6) {
      setPasswordMsg({ type: 'error', text: 'New password must be at least 6 characters' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordMsg({ type: 'error', text: 'New passwords do not match' });
      return;
    }
    setChangingPassword(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to change password');
      setPasswordMsg({ type: 'success', text: 'Password changed successfully!' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      await refreshUser();
    } catch (error) {
      setPasswordMsg({ type: 'error', text: error.message });
    }
    setChangingPassword(false);
  };

  if (!user) {
    navigate('/auth');
    return null;
  }

  const getStatusBadge = () => {
    switch (user.subscription_status) {
      case 'active':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300 text-sm font-medium">
            <Check className="w-3 h-3" />
            Active
          </span>
        );
      case 'gifted':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300 text-sm font-medium">
            <Gift className="w-3 h-3" />
            Gifted Access
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 text-sm font-medium">
            <X className="w-3 h-3" />
            Cancelled
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300 text-sm font-medium">
            No Subscription
          </span>
        );
    }
  };

  return (
    <div className="min-h-screen noise-bg p-6" data-testid="account-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(-1)}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-2xl font-bold">My Account</h1>
          </div>
          <Button variant="outline" onClick={handleLogout} data-testid="logout-btn">
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>

        {/* Cancel Success Message */}
        {cancelSuccess && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-emerald-100 dark:bg-emerald-900 border border-emerald-200 dark:border-emerald-800 rounded-xl text-emerald-700 dark:text-emerald-300"
          >
            <Check className="w-5 h-5 inline mr-2" />
            Your subscription has been cancelled. Redirecting...
          </motion.div>
        )}

        {/* Profile Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card border-2 border-border rounded-2xl p-6 mb-6"
        >
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <User className="w-5 h-5" />
            Profile
          </h2>
          
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                <User className="w-5 h-5 text-accent" />
              </div>
              <div>
                <p className="font-medium">{user.name}</p>
                <p className="text-sm text-muted-foreground">{user.email}</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Change Password Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-card border-2 border-border rounded-2xl p-6 mb-6"
        >
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Lock className="w-5 h-5" />
            Change Password
          </h2>

          {passwordMsg && (
            <div className={`mb-4 p-3 rounded-lg text-sm font-medium ${passwordMsg.type === 'success' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`} data-testid="password-msg">
              {passwordMsg.text}
            </div>
          )}

          <div className="space-y-3">
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Current Password</label>
              <Input
                type="password"
                placeholder="Enter current password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                data-testid="current-password-input"
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">New Password</label>
              <Input
                type="password"
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                data-testid="new-password-input"
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Confirm New Password</label>
              <Input
                type="password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                data-testid="confirm-password-input"
              />
            </div>
            <Button
              onClick={handleChangePassword}
              disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
              className="w-full bg-accent hover:bg-accent/90"
              data-testid="change-password-btn"
            >
              {changingPassword ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
              Update Password
            </Button>
          </div>
        </motion.div>

        {/* Subscription Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-card border-2 border-border rounded-2xl p-6 mb-6"
        >
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Subscription
          </h2>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Status</span>
              {getStatusBadge()}
            </div>
            
            {user.has_access && (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Plan</span>
                  <span className="font-medium">$5/month</span>
                </div>
                
                {user.subscription_status === 'active' && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Access</span>
                    <span className="text-emerald-600 dark:text-emerald-400 font-medium">
                      Full Access
                    </span>
                  </div>
                )}
              </>
            )}

            {!user.has_access && user.subscription_status !== 'cancelled' && (
              <div className="pt-4">
                <Button 
                  onClick={() => navigate('/subscribe')}
                  className="w-full bg-accent hover:bg-accent/90"
                  data-testid="subscribe-btn"
                >
                  Subscribe Now - $5/month
                </Button>
              </div>
            )}

            {user.subscription_status === 'cancelled' && (
              <div className="pt-4">
                <p className="text-sm text-muted-foreground mb-3">
                  Your subscription has been cancelled. You can resubscribe anytime.
                </p>
                <Button 
                  onClick={() => navigate('/subscribe')}
                  className="w-full bg-accent hover:bg-accent/90"
                  data-testid="resubscribe-btn"
                >
                  Resubscribe - $5/month
                </Button>
              </div>
            )}
          </div>
        </motion.div>

        {/* Cancel Subscription Section */}
        {user.has_access && user.subscription_status !== 'gifted' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-card border-2 border-destructive/20 rounded-2xl p-6"
          >
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Cancel Subscription
            </h2>
            
            <p className="text-muted-foreground mb-4">
              If you cancel, you'll lose access to all practice features immediately. 
              You can resubscribe at any time.
            </p>

            {cancelError && (
              <p className="text-sm text-destructive mb-4">{cancelError}</p>
            )}

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button 
                  variant="outline" 
                  className="border-destructive text-destructive hover:bg-destructive/10"
                  disabled={cancelling}
                  data-testid="cancel-subscription-btn"
                >
                  {cancelling ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <X className="w-4 h-4 mr-2" />
                  )}
                  Cancel My Subscription
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will immediately cancel your subscription and revoke access to practice features. 
                    You won't be charged again, but you'll need to resubscribe to regain access.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="cancel-dialog-no">Keep Subscription</AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={handleCancelSubscription}
                    className="bg-destructive hover:bg-destructive/90"
                    data-testid="cancel-dialog-yes"
                  >
                    Yes, Cancel Subscription
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </motion.div>
        )}

        {/* Gifted users can't cancel */}
        {user.subscription_status === 'gifted' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-2xl p-6"
          >
            <div className="flex items-center gap-2 mb-2">
              <Gift className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              <h3 className="font-semibold text-purple-700 dark:text-purple-300">Gifted Access</h3>
            </div>
            <p className="text-sm text-purple-600 dark:text-purple-400">
              You have free access through an access code. Enjoy your practice!
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Account;
